import { Static, Type } from '@sinclair/typebox'

/**
 * Interactive cards the AI Builder can put in the chat. The orchestrator is
 * stateless: a reply is re-associated by re-deriving `cardId` from the flow,
 * never by looking a card up in a store.
 */

export const CardDropdownOption = Type.Object({
    label: Type.String(),
    value: Type.Unknown(),
})
export type CardDropdownOption = Static<typeof CardDropdownOption>

export const ConnectionChoiceCard = Type.Object({
    cardId: Type.String(),
    type: Type.Literal('connection_gate'),
    stepName: Type.String(),
    pieceName: Type.String(),
    pieceDisplayName: Type.String(),
    /** Existing candidate connections; empty means "create one" is the only path */
    connections: Type.Array(
        Type.Object({
            externalId: Type.String(),
            displayName: Type.String(),
        }),
    ),
})
export type ConnectionChoiceCard = Static<typeof ConnectionChoiceCard>

/** Options a choice card shows before any search — the server's shortlist size */
export const AI_BUILDER_CARD_VISIBLE_OPTIONS = 5

export const DropdownChoiceCard = Type.Object({
    cardId: Type.String(),
    type: Type.Literal('dropdown_choice'),
    stepName: Type.String(),
    propName: Type.String(),
    propDisplayName: Type.String(),
    /** Shown up front: the options matching the target the user named, else the first ones */
    shortlist: Type.Array(CardDropdownOption),
    /** Full list for the card's search / "show more" */
    allOptions: Type.Array(CardDropdownOption),
})
export type DropdownChoiceCard = Static<typeof DropdownChoiceCard>

export const WritePreviewCard = Type.Object({
    cardId: Type.String(),
    type: Type.Literal('write_preview'),
    stepName: Type.String(),
    actionDisplayName: Type.String(),
    /** The exact input the step would run with */
    input: Type.Record(Type.String(), Type.Unknown()),
    outputIsRead: Type.Optional(Type.Boolean()),
})
export type WritePreviewCard = Static<typeof WritePreviewCard>

export const AiBuilderCard = Type.Union([
    ConnectionChoiceCard,
    DropdownChoiceCard,
    WritePreviewCard,
])
export type AiBuilderCard = Static<typeof AiBuilderCard>

export const cardIdFor = (
    type: AiBuilderCard['type'],
    stepName: string,
    propName?: string,
): string =>
    propName ? `${type}:${stepName}:${propName}` : `${type}:${stepName}`

/** The two answers a write-preview card accepts */
export const WRITE_PREVIEW_REPLY = {
    TEST: 'test',
    SKIP: 'skip',
} as const

export const CardReply = Type.Object({
    cardId: Type.String(),
    value: Type.Unknown(),
})
export type CardReply = Static<typeof CardReply>
